import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

interface AddPlanModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (planName: string, displayName: string, isAddOn: boolean) => void;
  isSubmitting: boolean;
}

const AddPlanModal: React.FC<AddPlanModalProps> = ({
  isOpen,
  onOpenChange,
  onConfirm,
  isSubmitting
}) => {
  const [planName, setPlanName] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [isAddOn, setIsAddOn] = useState<boolean>(false);

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setPlanName('');
      setDisplayName('');
      setIsAddOn(false);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(planName.trim(), displayName.trim() || planName.trim(), isAddOn);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add New Plan</DialogTitle>
            <DialogDescription>
              Enter the details for the new pricing plan.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="planName" className="text-right">
                Plan Name*
              </Label>
              <Input
                id="planName"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                className="col-span-3"
                placeholder="e.g. Basic, Pro, Enterprise"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="displayName" className="text-right">
                Display Name
              </Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="col-span-3"
                placeholder="Leave blank to use Plan Name"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="text-right col-span-1">
                <Label htmlFor="isAddOn">Add-on</Label>
              </div>
              <div className="flex items-center space-x-2 col-span-3">
                <Checkbox
                  id="isAddOn"
                  checked={isAddOn}
                  onCheckedChange={(checked) => setIsAddOn(checked === true)}
                />
                <Label htmlFor="isAddOn" className="text-sm font-normal">
                  This is an add-on plan
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!planName.trim() || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Plan'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPlanModal;
